'use client';

import { useCallback, useRef } from 'react';
import { useScrollBlock } from '../../../hooks/useScrollBlock';
import { sendMetrics } from '../../../services/ga.service';
import './CheckoutPopup.css';

// Close icon (white X), inlined as a data URI — same asset the converter's
// checkout popup has always used.
const CLOSE_ICON_DATA_URI = 'data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgNTEyIDUxMiIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiBlbmFibGUtYmFja2dyb3VuZD0ibmV3IDAgMCA1MTIgNTEyIj48cGF0aCBkPSJNNDQzLjYgMzg3LjEgMzEyLjQgMjU1LjRsMTMxLjUtMTMwYzUuNC01LjQgNS40LTE0LjIgMC0xOS42bC0zNy40LTM3LjZjLTIuNi0yLjYtNi4xLTQtOS44LTQtMy43IDAtNy4yIDEuNS05LjggNEwyNTYgMTk3LjggMTI0LjkgNjguM2MtMi42LTIuNi02LjEtNC05LjgtNC0zLjcgMC03LjIgMS41LTkuOCA0TDY4IDEwNS45Yy01LjQgNS40LTUuNCAxNC4yIDAgMTkuNmwxMzEuNSAxMzBMNjguNCAzODcuMWMtMi42IDIuNi00LjEgNi4xLTQuMSA5LjggMCAzLjcgMS40IDcuMiA0LjEgOS44bDM3LjQgMzcuNmMyLjcgMi43IDYuMiA0LjEgOS44IDQuMSAzLjUgMCA3LjEtMS4zIDkuOC00LjFMMjU2IDMxMy4xbDEzMC43IDEzMS4xYzIuNyAyLjcgNi4yIDQuMSA5LjggNC4xIDMuNSAwIDcuMS0xLjMgOS44LTQuMWwzNy40LTM3LjZjMi42LTIuNiA0LjEtNi4xIDQuMS05LjgtLjEtMy42LTEuNi03LjEtNC4yLTkuN3oiIGZpbGw9IiNmZmZmZmYiIGNsYXNzPSJmaWxsLTAwMDAwMCI+PC9wYXRoPjwvc3ZnPg==';

/**
 * Build the PayPro checkout URL for the credits product, pre-filled with the
 * buyer's email. Shared so the converter and the generator never drift on the
 * product id / template / affiliate params.
 */
export function buildCreditsCheckoutUrl(email) {
  return `https://store.payproglobal.com/checkout?products[1][id]=114606&currency=EUR&page-template=20406&x-email=${encodeURIComponent(email || '')}&exfo=742&secret-key=uRw2jsmJ5y`;
}

/**
 * Shared "buy credits without leaving the page" checkout popup: a small modal
 * with a PayPro checkout iframe. First built for the converter
 * (app/converter/Controls.js) and extracted here unchanged so the generator
 * can open the exact same popup instead of sending people to /pricing.
 *
 * Deliberately modest, matching the converter's existing tone: nothing opens
 * on its own from this hook — every call site decides when `showCheckout`
 * runs (a click, or — for the generator's one auto-open case — a deliberate
 * call right after a result the user already saw).
 *
 * `source` and the `trigger` passed to `showCheckout` ride along on the
 * `show_checkout_popup` GA4 event so purchase intent started from the
 * generator can be measured separately from the converter's — see
 * AiGenerator.js and the CHANGELOG for the exact query.
 */
export function useCheckoutPopup({ checkoutUrl, userId, source }) {
  const [scrollBlock, allowScroll] = useScrollBlock();
  // Bound to the currently-open popup's Escape handler so hideCheckout can
  // remove exactly the listener it added, however the popup gets closed.
  const escHandlerRef = useRef(null);

  const hideCheckout = useCallback((e) => {
    e?.preventDefault?.();
    document.getElementById('ppg-checkout-modal')?.remove();
    if (escHandlerRef.current) {
      document.removeEventListener('keydown', escHandlerRef.current);
      escHandlerRef.current = null;
    }
    allowScroll();
  }, [allowScroll]);

  const showCheckout = useCallback((event, { trigger = 'click' } = {}) => {
    sendMetrics('show_checkout_popup', { user_id: userId, source, trigger });
    event?.preventDefault?.();

    // Never stack a second modal on top of an already-open one (matters for
    // the auto-open path, which could otherwise race a manual click).
    if (document.getElementById('ppg-checkout-modal')) return;

    scrollBlock();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') hideCheckout(e);
    };
    escHandlerRef.current = onKeyDown;
    document.addEventListener('keydown', onKeyDown);

    const modal = document.createElement('div');
    modal.classList.add('ppg-checkout-modal', 'ppg-show');
    modal.id = 'ppg-checkout-modal';

    const closeButton = document.createElement('div');
    closeButton.classList.add('ppg-btn-close');
    closeButton.id = 'ppg-btn-close';
    closeButton.addEventListener('click', hideCheckout);

    const closeIcon = document.createElement('img');
    closeIcon.src = CLOSE_ICON_DATA_URI;
    closeButton.appendChild(closeIcon);

    const loader = document.createElement('div');
    loader.classList.add('ppg-loader');

    const iframe = document.createElement('iframe');
    iframe.classList.add('ppg-iframe');
    iframe.id = 'ppg-iframe';
    iframe.src = checkoutUrl;
    iframe.frameBorder = '0';

    modal.append(closeButton, loader, iframe);
    document.body.appendChild(modal);

    iframe.onload = () => {
      loader.remove();
      closeButton.classList.add('ppg-show');
    };
  }, [checkoutUrl, userId, source, scrollBlock, hideCheckout]);

  return { showCheckout, hideCheckout };
}
